import { History } from 'history'
import { noop } from 'lodash'
import { EditorView } from 'prosemirror-view'
import { createContext, ReactNode, useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Dispatch } from 'redux'

import { useModals } from 'almanac-ui/Modal'
import { IStackManager } from 'almanac-ui/Modal/Types'
import { MinimalProject } from 'api/clients/ProjectClient'
import { ModalState, useModalStore } from 'hooks/stores/useModalStore'
import useImportModal from 'hooks/useImportModal'
import useNewEntrypointModal from 'hooks/useNewEntrypointModal'
import useReviewCreationModal from 'hooks/useReviewCreationModal'
import { AlmanacFile, ArticleFlat, ChannelInfo, HandbookV2, LayerSummary, RootState, Workspace } from 'types'
import { useIsBlaze } from 'hooks/useIsBlaze'

export type CommandLineContext = Partial<{
  article: ArticleFlat
  layer?: LayerSummary
  channel: Partial<ChannelInfo>
  handbook: HandbookV2
  dispatch: Dispatch
  editorView: EditorView
  folder: AlmanacFile | null
  history: History
  project: Partial<MinimalProject>
  state: RootState
  workspace: Partial<Workspace>
  openNewReviewModal: ReturnType<typeof useReviewCreationModal>['openReviewModal']
  openNewEntrypointModal: ReturnType<typeof useNewEntrypointModal>['openNewEntrypointModal']
  openImportModal: ReturnType<typeof useImportModal>['openImportModal']
  openModal: ModalState['openModal']
  closeModal: ModalState['closeModal']
  openAlmanacModal: IStackManager['openModal']
  closeAlmanacModal: IStackManager['closeModal']
  isBlaze?: boolean
}>

export type CommandLineReactContextType = {
  addContext: <T extends keyof CommandLineContext>(key: T, value: CommandLineContext[T]) => void
  context: CommandLineContext
  removeContext: <T extends keyof CommandLineContext>(key: T) => void
}

export const CommandLine = createContext<CommandLineReactContextType>({
  addContext: noop,
  context: {},
  removeContext: noop,
})

export const CommandLineWrapper = ({ children }: { children: ReactNode }) => {
  // NOTE: global context entries can be added here
  const dispatch = useDispatch()
  const history = useHistory()
  const isBlaze = useIsBlaze()
  const { openModal: openAlmanacModal, closeModal: closeAlmanacModal } = useModals()
  const openModal = useModalStore((state) => state.openModal)
  const closeModal = useModalStore((state) => state.closeModal)
  const [context, setContext] = useState<CommandLineContext>({
    dispatch,
    history,
    openModal,
    closeModal,
    openAlmanacModal,
    closeAlmanacModal,
    isBlaze,
  })

  const addContext = useCallback(<T extends keyof CommandLineContext>(key: T, value: CommandLineContext[T]) => {
    // NOTE: if you are extending the context it can be useful to check if there
    // is no infinite loop by uncommenting the following line!
    setContext((context) => {
      const newContext = Object.assign({}, context)
      newContext[key] = value

      return newContext
    })
  }, [])

  const removeContext = useCallback(<T extends keyof CommandLineContext>(key: T) => {
    setContext((context) => {
      if (!context[key]) return context

      const newContext = Object.assign({}, context)
      delete newContext[key]

      return newContext
    })
  }, [])

  return <CommandLine.Provider value={{ addContext, context, removeContext }}>{children}</CommandLine.Provider>
}
